import React from 'react';
import { Link } from 'react-router-dom';
import { animated, useTransition } from '@react-spring/web';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faRibbon } from '@fortawesome/pro-solid-svg-icons/faRibbon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAnimatedRouteState } from '@wildidea/components/AnimatedRoutes/AnimatedRoutes';
import { LinkButton } from '@wildidea/components/Button/LinkButton';
import { Card } from '@wildidea/components/Card/Card';
import { useThemeAnimations } from '@wildidea/components/Theme/Theme';

import { GetTheAppCTA } from '@components/GetTheAppCTA.js';

import jon from './img/jon.webp';
import raquel from './img/raquel.webp';

import * as css from './AboutRoute.module.css';

const AboutRoute: React.FC<{}> = () => {
  const { phase, onTransitionEnd } = useAnimatedRouteState();
  const active = phase === 'enter' || phase === 'active';
  const { fromBelow } = useThemeAnimations('Common');
  const contentTransition = useTransition(active, { ...fromBelow, onRest: onTransitionEnd });
  return contentTransition((style, active) =>
    active ? (
      <animated.div style={style} className={css.layout}>
        <div className={css.pageHeader}>
          <Link to="/" aria-label="Back">
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
          <h1>About Us</h1>
        </div>

        <section className={css.hero}>
          <h2>Every responder deserves an ally.</h2>
          <div className={css.heroSub}>
            <div>Get to know the creators of Rescue Ally.</div>
          </div>
        </section>

        <section className={css.bios}>
          <Card
            color="inverse"
            className={css.bioCard}
            figure={<img src={raquel} className={css.profilePhoto} />}
            fullBleed
            orientation="portrait"
          >
            <h2>Raquel Sapp - Austere Medicine Educator</h2>
            <p>
              My work sits at the intersection of wilderness medicine, emergency and critical care,
              outdoor education, risk management and global health, with a particular interest in
              how people make decisions and care for one another when resources are limited.
            </p>
            <p>
              For more than 15 years, I’ve worked across EMS, nursing, wilderness and mountain
              rescue, climbing instruction and outdoor education. That experience has taken me from
              ambulances and hospitals to remote field environments, technical rescues and
              wilderness medicine classrooms throughout North America and internationally.
            </p>
            <p>
              I founded <a href="https://backcountrypulse.org">Backcountry Pulse</a> in 2018 after
              years of seeing areas of wilderness medicine education that I believed could be
              approached differently. At the center of that work is the belief that high-quality
              wilderness medicine education should be accessible, evidence-based and relevant to the
              communities actually using it.
            </p>
            <p>
              I’m especially interested in the parts of wilderness medicine that are harder to fit
              neatly into an algorithm. Communication, culture, stress, decision-making, compassion
              and responder wellbeing all influence how we assess, respond and care for people in
              difficult situations. My broader work includes risk management, emergency planning and
              helping outdoor organizations think more holistically about physical and psychological
              safety.
            </p>
            <p>
              Rescue Ally grew out of many of those same curiously eager conversations. Jon and I
              wanted to build something practical for the realities of rescue and remote medicine,
              where providers may be cold, tired, managing several problems at once and working
              without the resources they would normally have available. We each bring unique
              backgrounds to the project and share an interest in making useful information easier
              to access when it matters.
            </p>
            <p>
              One of my favorite parts of this work is still the moment when someone realizes they
              are more capable than they thought they were. So here’s to helping individuals, teams
              and communities become more capable, more prepared and better equipped to care for one
              another.
            </p>
          </Card>

          <Card
            color="inverse"
            className={css.bioCard}
            figure={<img src={jon} className={css.profilePhoto} />}
            fullBleed
            orientation="portrait"
          >
            <h2>Jon Hieb - App Developer</h2>
            <h3>Adventurer | Father | Engineer</h3>
            <p>
              As a mountaineer, trail runner, and climber, I'm always seeking the next big
              challenge. As a husband and father of 3 amazing children, I'm committed to staying
              alive.
            </p>
            <p>
              I'm a certified Wilderness First Responder and applied the techniques in real-life
              situations more than once.{' '}
              <a href="https://linkedin.com/in/hiebj">I've been building software since 2010</a>,
              including a stint at <a href="https://caltopo.com">CalTopo</a>, and I've always felt
              like there was a gap in technology for austere medicine, but needed the partnership of
              a genuine expert to build something that would actually serve the needs of rescuers.
            </p>
            <p>
              When I took WFR recertification from Raquel and her business{' '}
              <a href="https://backcountrypulse.org">Backcountry Pulse</a> in 2024, a student asked
              if there was an app, and Raquel mentioned that nothing really existed that filled the
              need. When she asked if anyone in the class knew any app developers, I immediately
              raised my hand. Discussing with her afterwards, and feeling the energy, it was
              immediately apparent that I'd found the business partner I'd been looking for.
            </p>
            <p>
              I grew up in an era of technology before social media, and used the internet in those
              early days before things got completely cynical. I still believe in a vision of
              technology where it is used to solve real problems that real people have, where people
              get what they pay for, and where we set ourselves apart by building the most useful
              product, not by using deceptive business tactics.
            </p>
            <p>
              Raquel and I worked together for nearly two years and countless hours to bring the
              first version of Rescue Ally to you. It's been a labor of dedication and passion from
              each of us, combining our talents and training to create something truly special. I'm
              proud of what we've built together. We've got a long way to go, and I look forward to
              taking Rescue Ally beyond, finding new ways for technology to assist with rescue in
              remote locations and difficult circumstances.
            </p>
          </Card>
        </section>

        <section className={css.cta}>
          <h2>Please join us.</h2>
          <h3>Help us make this vision a reality.</h3>
          <GetTheAppCTA className={css.getTheApp} />
          <LinkButton
            appearance="outline"
            color="info"
            to="/ambassadors"
            icon={<FontAwesomeIcon icon={faRibbon} />}
            className={css.ambassadorButton}
          >
            Become an Ambassador
          </LinkButton>
        </section>
      </animated.div>
    ) : null
  );
};

export default AboutRoute;
